import dayjs from "dayjs";

const addZero = (number: number) => {
    return number < 10 ? `0${number}` : number;
}

export const formatDate = (inputDate: Date) => {
    return `${addZero(inputDate.getMonth()+1)}-${addZero(inputDate.getDate())}-${inputDate.getFullYear()} ${addZero(inputDate.getHours())}:${addZero(inputDate.getMinutes())}`
}

export const formatDateNoTime = (inputDate: Date) => {
    return `${addZero(inputDate.getMonth()+1)}-${addZero(inputDate.getDate())}-${inputDate.getFullYear()}`
}

export const shortFormatDate = (inputDate: Date) => {
    return `${inputDate.getFullYear().toString().substr(-2)}-${addZero(inputDate.getMonth()+1)}-${addZero(inputDate.getDate())} ${addZero(inputDate.getHours())}:${addZero(inputDate.getMinutes())}`
}

export const shortFormatDateNoTime = (inputDate: Date) => {
    return `${inputDate.getFullYear().toString().substr(-2)}-${addZero(inputDate.getMonth()+1)}-${addZero(inputDate.getDate())}`
}

export const formatTimeNoDate = (inputDate: Date) => {
    return `${addZero(inputDate.getHours())}:${addZero(inputDate.getMinutes())}`
}

export const formatDateWithAMPM = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };

    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

    // Split formatted date into parts to rearrange
    const [month, day, year] = formattedDate.split('/');
    const [time, modifier] = formattedDate.split(', ')[1].trim().split(' ');

    // Return the formatted string as "YYYY-MM-DD hh:mm AM/PM"
    return `${year}-${month}-${day} ${time} ${modifier}`;
}

export const formatDateForAmPmDisplay = (date: Date): string => {
    return dayjs(date).format('MM-DD-YYYY hh:mmA');
};

export const getFriendlyDate = (inputDate: Date) => {
    const now = new Date();
    const timeDifference = now.getTime() - inputDate.getTime();
    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    if (seconds < 10) {
        return "just now";
    } else if (seconds < 60) {
        return `${seconds} seconds ago`;
    } else if (minutes === 1) {
        return "a minute ago";
    } else if (minutes < 60) {
        return `${minutes} minutes ago`;
    } else if (hours === 1) {
        return "an hour ago";
    } else if (hours < 24) {
        return `${hours} hours ago`;
    } else if (days === 1) {
        return "yesterday";
    } else if (days < 30) {
        return `${days} days ago`;
    } else if (months === 1) {
        return "a month ago";
    } else if (months < 12) {
        return `${months} months ago`;
    } else if (years === 1) {
        return "a year ago";
    } else {
        return `${years} years ago`;
    }
}

export const getFriendlyTimeDifference = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if(years) {
        return `Over ${years} years`;
    }
    if(months) {
        return `Over ${months} months`;
    }
    if(weeks) {
        return `Over ${weeks} weeks`;
    }
    if(days) {
        const hoursLeft = hours-(days*24);
        return `${days} day${days>1 ? 's' : ''}${hoursLeft ? ` and ${hoursLeft} hour${hoursLeft > 1 ? 's' : ''}` : ''}`;
    }
    if(hours) {
        const minutesLeft = minutes-(hours*60);
        return `${hours} hour${hours>1 ? 's' : ''}${minutesLeft ? ` and ${minutesLeft} minute${minutesLeft > 1 ? 's' : ''}` : ''}`;
    }
    if(minutes) {
        const secondsLeft = seconds-(minutes*60);
        return `${minutes} minute${minutes > 1 ? 's' : ''}${secondsLeft ? ` and ${secondsLeft} seconds` : ''}`;
    }
    if(seconds) {
        return `${seconds} second${seconds > 1 ? 's' : ''}`;
    }
}

export const dateDaysAgo = (value: number, date: Date = new Date()) => {
    return new Date(new Date().setDate(date.getDate() - value));
}

export const daysBetweenDates = (str1: string, str2: string): number => {
    const diff = Date.parse(str2) - Date.parse(str1);
    return Math.ceil(diff / 86400000);
}

export const millisecondsToHuman = (milliseconds: number) => {
    // TIP: to find current time in milliseconds, use:
    // const  current_time_milliseconds = new Date().getTime();

    function numberEnding (number: number) {
        return (number > 1) ? 's' : '';
    }

    let temp = Math.floor(milliseconds / 1000);
    const years = Math.floor(temp / 31536000);
    if (years) {
        return years + ' year' + numberEnding(years);
    }
    //TODO: Months! Maybe weeks?
    const days = Math.floor((temp %= 31536000) / 86400);
    if (days) {
        return days + ' day' + numberEnding(days);
    }
    const hours = Math.floor((temp %= 86400) / 3600);
    if (hours) {
        return hours + ' hour' + numberEnding(hours);
    }
    const minutes = Math.floor((temp %= 3600) / 60);
    if (minutes) {
        return minutes + ' minute' + numberEnding(minutes);
    }
    const seconds = temp % 60;
    if (seconds) {
        return seconds + ' second' + numberEnding(seconds);
    }
    return 'less than a second'; //'just now' //or other string you like;
}

export const getFriendlyTimeDifferenceV2 = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffHours / 24;

    if (diffHours < 24) {
        // Rule 1: Less than 24 hours - return hh:mm format
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    } else if (diffDays < 2) {
        // Rule 2: Over a day, but less than 2 days
        return "yesterday";
    } else if (diffDays < 7) {
        // Rule 3: Over 48 hours, but less than a week
        return date.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
        // Rule 4: Over a week
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
    }
}